/**
 * Smart Lexicon - Main Application
 *
 * React Router setup with root routes.
 * AuthProvider wraps the app for authentication state.
 * RequireAuth guards protected routes with login redirect.
 */

import { Suspense, lazy, type ComponentType, type LazyExoticComponent } from 'react'
import { Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { motion, useReducedMotion } from 'motion/react'
import { AuthProvider } from './contexts/AuthContext'
import { RequireAuth } from './components/auth/RequireAuth'
import { LoginPage } from './components/auth/LoginPage'
import { AuthCallback } from './components/auth/AuthCallback'
import { AppShell } from './components/AppShell'
import { LegalView } from './components/legal/LegalView'
import { BlogView } from './components/blog/BlogView'
import { BlogIndexView } from './components/blog/BlogIndexView'
import { BlogClusterView } from './components/blog/BlogClusterView'
import { WordCardsKo } from './components/landing/WordCardsKo'
import { LandingView } from './components/landing/LandingView'
import { isQuickLookup } from './lib/quick-lookup'

/**
 * Route-level code split (2026-08-30). The /app/* views are lazy: landing
 * visitors (the promo traffic) never open them, and they sit behind auth
 * where the static AppShell paints first — fallback=null means the content
 * area is briefly empty while the chunk loads, not a blank page.
 *
 * Deliberately NOT lazy: LandingView/BlogView/LegalView — their pages are
 * CONTENT-prerendered (scripts/prerender.mjs), and React 19 createRoot
 * replaces #root on first render, so a suspending view would blank the
 * already-painted prerendered HTML until its chunk arrived. LoginPage too
 * (extension activation path — no chunk waterfall before auth).
 */
const lazyNamed = <P extends object = Record<string, never>>(
  load: () => Promise<Record<string, unknown>>,
  name: string,
): LazyExoticComponent<ComponentType<P>> =>
  lazy(async () => ({ default: (await load())[name] as ComponentType<P> }))

const GenerateView = lazyNamed(() => import('./components/views/GenerateView'), 'GenerateView')
const ReviewView = lazyNamed(() => import('./components/views/ReviewView'), 'ReviewView')
const LibraryView = lazyNamed(() => import('./components/views/LibraryView'), 'LibraryView')
const DocumentImportView = lazyNamed(() => import('./components/views/DocumentImportView'), 'DocumentImportView')
const QuickSaveView = lazyNamed(() => import('./components/views/QuickSaveView'), 'QuickSaveView')
const MediaImportView = lazyNamed(() => import('./components/views/MediaImportView'), 'MediaImportView')
const VideoLibraryView = lazyNamed(() => import('./components/views/VideoLibraryView'), 'VideoLibraryView')
const VideoStudyView = lazyNamed(() => import('./components/views/VideoStudyView'), 'VideoStudyView')
const SettingsView = lazyNamed(() => import('./components/views/SettingsView'), 'SettingsView')
const StatsView = lazyNamed(() => import('./components/views/StatsView'), 'StatsView')
const ReferralTestPage = lazyNamed(() => import('./components/test/ReferralTestPage'), 'ReferralTestPage')

/**
 * /app/import route wrapper — quick-path split (extension-activation-quickpath
 * .md P0-1). A short extension lookup (word/phrase/subtitle line, via=extension,
 * ≤120 chars) reroutes to the one-step QuickSaveView; the two-step workbench is
 * a document-mining mental model, not dictionary lookup, and it was where every
 * extension signup churned (2026-08-26/27 sessions). Long texts and manual
 * pastes (no via) always stay in the workbench. Split lives at the route level
 * so DocumentImportView's hook order is unconditional.
 */
function ImportRoute() {
  const [searchParams] = useSearchParams()
  if (isQuickLookup(searchParams.get('text'), searchParams.get('via'))) {
    return <Navigate to={`/app/quick?${searchParams.toString()}`} replace />
  }
  return <DocumentImportView />
}
import { PublicLocaleGate } from './lib/PublicLocaleGate'

/**
 * AppShellRoutes — the /app/* view switch, wrapped in a pathname-keyed fade.
 * Direction A: kills the instant cut between Generate/Review/Library/Stats
 * (the single biggest "old app" tell from the motion audit). Fade-in only
 * (no exit animation) so navigation has zero added latency and no layout
 * overlap; reduced-motion users get an instant swap.
 */
function AppShellRoutes() {
  const location = useLocation()
  const prefersReduced = useReducedMotion()
  return (
    <motion.div
      key={location.pathname}
      initial={prefersReduced ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
    >
      <Suspense fallback={null}>
      <Routes location={location}>
        <Route path="/" element={<GenerateView />} />
        <Route path="/review" element={<ReviewView />} />
        <Route path="/library" element={<LibraryView />} />
        <Route path="/import" element={<ImportRoute />} />
        <Route path="/quick" element={<QuickSaveView />} />
        <Route path="/media" element={<VideoLibraryView />} />
        <Route path="/media/new" element={<MediaImportView />} />
        <Route path="/media/:jobId" element={<VideoStudyView />} />
        <Route path="/stats" element={<StatsView />} />
        <Route path="/settings" element={<SettingsView />} />
        <Route path="redeem" element={<Navigate to="/app/settings" replace />} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
      </Suspense>
    </motion.div>
  )
}

/** Public routes — mounted at `/` and at `/:locale/*` (multilingual-seo.md
 *  M2). RELATIVE paths only: react-router forbids absolute child paths under
 *  a param parent. `:locale` ∈ PUBLIC_LOCALES; views strip the prefix via
 *  usePublicPath (blog/legal lookups, internal links). */
function PublicRoutes() {
  return (
    <>
      <Route path="login" element={<LoginPage />} />
      <Route path="auth/callback" element={<AuthCallback />} />
      <Route path="test/referral" element={<RequireAuth><Suspense fallback={null}><ReferralTestPage /></Suspense></RequireAuth>} />
      <Route path="privacy" element={<LegalView />} />
      <Route path="terms" element={<LegalView />} />
      <Route path="refund" element={<LegalView />} />
      {/* Blog hub — mounted at /blog/ AND /<locale>/blog/ via the double
          PublicRoutes() mount below (BlogIndexView picks its list by prefix). */}
      <Route path="blog" element={<BlogIndexView />} />
      {/* Cluster (topic) hubs — same double-mount family; unknown clusters
          and cluster/locale combos without content redirect to /blog. */}
      <Route path="blog/c/exam-vocab" element={<BlogClusterView />} />
      <Route path="blog/c/vocab-notebook" element={<BlogClusterView />} />
      <Route path="blog/c/srs-method" element={<BlogClusterView />} />
      <Route path="blog/c/tool-picking" element={<BlogClusterView />} />
      <Route path="blog/c/youtube-capture" element={<BlogClusterView />} />
      <Route path="blog/c/card-making" element={<BlogClusterView />} />
      <Route path="blog/c/language-reactor" element={<BlogClusterView />} />
      <Route path="blog/english-vocab-without-anki" element={<BlogView />} />
      <Route path="blog/youtube-english-vocab-organize" element={<BlogView />} />
      <Route path="blog/english-dictionary-app-guide" element={<BlogView />} />
      <Route path="blog/youtube-english-fix" element={<BlogView />} />
      <Route path="blog/language-reactor-alternatives" element={<BlogView />} />
      <Route path="blog/youtube-eikaiwa-study-method" element={<BlogView />} />
      <Route path="blog/youtube-vocab-cards-from-youtube" element={<BlogView />} />
      <Route path="blog/english-word-card-comparison" element={<BlogView />} />
      <Route path="blog/save-words-from-youtube-subtitles" element={<BlogView />} />
      <Route path="blog/language-reactor-export-to-anki" element={<BlogView />} />
      <Route path="blog/youtube-eikaiwa-self-study" element={<BlogView />} />
      <Route path="blog/english-vocab-memorization-methods" element={<BlogView />} />
      <Route path="blog/ielts-vocabulary-notebook" element={<BlogView />} />
      <Route path="blog/core-1200-vocab-practice" element={<BlogView />} />
      <Route path="blog/toeic-vocab-building" element={<BlogView />} />
      <Route path="blog/english-word-card-how-to" element={<BlogView />} />
      <Route path="blog/civil-service-english-vocab" element={<BlogView />} />
      <Route path="blog/phrasal-verbs-notebook" element={<BlogView />} />
      <Route path="blog/english-vocab-list-download" element={<BlogView />} />
      <Route path="blog/ket-vocabulary-prep" element={<BlogView />} />
      <Route path="blog/memorize-english-words-fast" element={<BlogView />} />
      <Route path="blog/memorize-english-sentences" element={<BlogView />} />
      <Route path="blog/english-vocab-notebook-pdf" element={<BlogView />} />
      <Route path="blog/english-vocab-sites" element={<BlogView />} />
      <Route path="blog/toeic-vocab-pdf" element={<BlogView />} />
      <Route path="blog/suneung-english-vocab" element={<BlogView />} />
      <Route path="blog/conversation-vocab-books" element={<BlogView />} />
      <Route path="blog/english-conversation-self-study" element={<BlogView />} />
      <Route path="blog/toefl-vocab" element={<BlogView />} />
      <Route path="blog/how-to-memorize" element={<BlogView />} />
      <Route path="blog/english-vocab-notebook" element={<BlogView />} />
      <Route path="blog/hackers-toeic-vocab" element={<BlogView />} />
      <Route path="blog/suneung-vocab-books" element={<BlogView />} />
      <Route path="blog/high-school-english-vocab-pdf" element={<BlogView />} />
      <Route path="blog/suneung-english-study-method" element={<BlogView />} />
      <Route path="blog/toeic-eitango" element={<BlogView />} />
      <Route path="blog/eiken-eitango" element={<BlogView />} />
      <Route path="blog/chugaku-eitango" element={<BlogView />} />
      <Route path="blog/english-vocab-books" element={<BlogView />} />
      <Route path="blog/english-vocabulary" element={<BlogView />} />
      <Route path="blog/which-words-to-learn" element={<BlogView />} />
      <Route path="blog/anki-alternative-youtube-vocab" element={<BlogView />} />
      <Route path="blog/jeonip-english-vocab-pdf" element={<BlogView />} />
      <Route path="blog/sistan-eitango-app" element={<BlogView />} />
      <Route path="blog/suteuk-english-vocab" element={<BlogView />} />
      <Route path="blog/eitango-oboekata" element={<BlogView />} />
      <Route path="blog/english-listening-study-method" element={<BlogView />} />
      <Route path="blog/middle-school-english-vocab" element={<BlogView />} />
      <Route path="blog/toeic-speaking-study-method" element={<BlogView />} />
      <Route path="blog/english-speaking-study-method" element={<BlogView />} />
      <Route path="blog/kaishi-1-5k-japanese-anki-deck" element={<BlogView />} />
      <Route path="blog/best-anki-decks-japanese" element={<BlogView />} />
      <Route path="blog/hsk-1-vocabulary" element={<BlogView />} />
      <Route path="blog/toeic-lc-study-method" element={<BlogView />} />
      <Route path="blog/elementary-english-vocab" element={<BlogView />} />
      <Route path="blog/jlpt-n5-vocabulary" element={<BlogView />} />
      <Route path="blog/english-error-notebook" element={<BlogView />} />
      <Route path="blog/teps-vocab" element={<BlogView />} />
      <Route path="blog/most-common-japanese-words" element={<BlogView />} />
      <Route path="blog/practice-reading-korean-words" element={<BlogView />} />
      <Route path="blog/english-study-apps" element={<BlogView />} />
      <Route path="blog/duolingo-anki-deck-japanese" element={<BlogView />} />
      <Route path="blog/hsk-4-vocabulary" element={<BlogView />} />
      <Route path="blog/basic-english-vocab" element={<BlogView />} />
      <Route path="blog/hsk-3-vocabulary" element={<BlogView />} />
      <Route path="blog/lecture-to-anki" element={<BlogView />} />
      <Route path="blog/cool-english-alternative" element={<BlogView />} />
      <Route path="blog/hsk-2-vocabulary" element={<BlogView />} />
      <Route path="blog/anki-furigana-hide" element={<BlogView />} />
      <Route path="blog/genki-vocabulary" element={<BlogView />} />
      <Route path="blog/1000-essential-vocabulary-jlpt-n5" element={<BlogView />} />
      <Route path="blog/topik-vocabulary" element={<BlogView />} />
      <Route path="blog/pdf-to-anki" element={<BlogView />} />
      <Route path="blog/anki-spanish-deck" element={<BlogView />} />
      <Route path="blog/japanese-learning-apps" element={<BlogView />} />
      <Route path="blog/nihongo-practice-reading" element={<BlogView />} />
      <Route path="blog/notion-to-anki" element={<BlogView />} />
      <Route path="blog/quizlet-to-anki" element={<BlogView />} />
      <Route path="blog/how-many-new-anki-cards-per-day" element={<BlogView />} />
      <Route path="blog/best-youtube-channels-to-learn-korean" element={<BlogView />} />
      <Route path="welcome" element={<BlogView />} />
    </>
  )
}

function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Root landing page */}
        <Route path="/" element={<LandingView />} />

        {/* Public pages at the canonical en URLs. Called as functions, NOT
            <PublicRoutes /> — <Routes> only accepts <Route>/<Fragment> as
            element children; a component element fails that check at the
            type level (the "[TF] is not a <Route>" crash). The call returns
            a Fragment of Routes, which IS allowed. */}
        {PublicRoutes()}

        {/* Same public pages under a locale prefix (/ko/, /zh-TW/). More
            specific than the catch-all below, so router ranking picks it.
            The index route is the locale landing itself — without it /ko/
            matches the parent but renders an empty Outlet (the blank-page
            regression 500aeea introduced by deleting the shared index). */}
        <Route path="/:locale/*" element={<PublicLocaleGate />}>
          <Route index element={<LandingView />} />
          {PublicRoutes()}
        </Route>

        {/* KR money page (landing-v12 P1-③) — ko-ONLY, top-level absolute
            route OUTSIDE PublicRoutes: the double mount would clone it at
            /word-cards and under every other locale prefix (duplicate
            content). Sibling paths (/zh-TW/word-cards etc.) are unrouted —
            the worker 404s them like any unknown /<locale>/ path. */}
        <Route path="/ko/word-cards" element={<WordCardsKo />} />

        {/* Protected app routes */}
        <Route path="/app/*" element={
          <RequireAuth>
            <AppShell>
              <AppShellRoutes />
            </AppShell>
          </RequireAuth>
        } />

        {/* Legacy route redirects for bookmarks/deep links */}
        <Route path="/review" element={<Navigate to="/app/review" replace />} />
        <Route path="/library" element={<Navigate to="/app/library" replace />} />
        <Route path="/stats" element={<Navigate to="/app/stats" replace />} />
        <Route path="/settings" element={<Navigate to="/app/settings" replace />} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  )
}

export default App
